/**
 * Vertrouwenslaag-chip onder een Mila-antwoord, en in de legenda van het
 * welkomstpaneel.
 *
 * Stond eerst als vijf inline JSX-takken in Kennisagent.tsx. Hierheen gehaald
 * toen het welkomstpaneel dezelfde chips moest tonen: twee kopieën van deze
 * kleuren en labels lopen gegarandeerd uit de pas, en dat is precies hoe de
 * paneeltekst drie maanden lang bronnen bleef noemen die niet meer bestonden.
 */

/** Frontend mirror of shared/src/ring2-sources.ts RING2_SOURCE_LABELS.
 * Kept inline rather than imported because the chat surface stays a
 * pure presentational layer over the JSON the agent returns. */
export const RING2_SOURCE_LABELS: Record<string, string> = {
  nhg: 'NHG',
  trimbos: 'Trimbos',
  thuisarts: 'Thuisarts.nl',
  ggz_standaarden: 'GGZ-standaarden',
  rivm: 'RIVM',
  behandelhulp: 'Behandelhulp.nl',
};

const BASE = 'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px]';

/** Eén definitie per laag: label, tooltip en kleuren. De volgorde hier is de
 * volgorde waarin de legenda ze toont, van eigen materiaal naar extern. */
export const RING_CHIPS = {
  ring1: {
    label: 'SUPPOHRT',
    title: 'SUPPOHRT-eigen kennisbank corpus.',
    uitleg: 'Eigen materiaal, beheerd door SUPPOHRT.',
    cls: `${BASE} border-ring1-border bg-ring1-bg font-medium text-ring1-fg`,
    dot: 'bg-ring1-fg',
  },
  ring2: {
    label: 'Geverifieerde bron',
    title: 'Geverifieerde externe bron — wekelijks gecrawld door SUPPOHRT.',
    uitleg:
      'Geverifieerde externe richtlijnen en publieksinformatie. De chip noemt de bron zelf, bijvoorbeeld Trimbos of Thuisarts.nl.',
    cls: `${BASE} border-ring2-border bg-ring2-bg font-medium text-ring2-fg`,
    dot: 'bg-ring2-fg',
  },
  ring3: {
    label: 'Werkbladen',
    title:
      'Werkblad uit het Meesters in de Psychologie tools-archief — wekelijks gecrawld door SUPPOHRT.',
    uitleg: 'Praktijkwerkbladen uit het tools-archief van Meesters in de Psychologie.',
    cls: `${BASE} border-ring2-border bg-ring2-bg font-medium text-ring2-fg`,
    dot: 'bg-ring2-fg',
  },
  ring0: {
    label: 'SUPPOHRT Intern',
    title: 'SUPPOHRT-intern document — alleen zichtbaar voor SUPPOHRT-medewerkers.',
    uitleg: 'Interne documenten, alleen zichtbaar voor SUPPOHRT-medewerkers.',
    cls: `${BASE} border-slate-300 bg-slate-100 font-medium text-slate-800`,
    dot: 'bg-slate-700',
  },
  ring_hq: {
    label: 'SUPPOHRT Kantoor',
    title:
      'Kantoorpersoneel-document — interne processen + beleid, alleen zichtbaar voor SUPPOHRT-kantoorpersoneel.',
    uitleg: 'Interne processen en beleid, alleen zichtbaar voor het kantoorpersoneel.',
    cls: `${BASE} border-slate-400 bg-slate-200 font-semibold text-slate-900`,
    dot: 'bg-slate-900',
  },
} as const;

export type RingKey = keyof typeof RING_CHIPS;

/** `ring` is optioneel omdat oude chats (pre-slice-5) het veld niet dragen;
 * die vallen terug op Ring 1, net als voorheen in Kennisagent.tsx. */
export function RingChip({
  ring,
  ring2Source,
  className,
}: {
  ring?: string | null;
  ring2Source?: string | null;
  className?: string;
}): JSX.Element {
  const key: RingKey = ring && ring in RING_CHIPS ? (ring as RingKey) : 'ring1';
  const chip = RING_CHIPS[key];
  const label =
    key === 'ring2' && ring2Source && RING2_SOURCE_LABELS[ring2Source]
      ? RING2_SOURCE_LABELS[ring2Source]
      : key === 'ring2'
        ? 'Ring 2'
        : chip.label;
  return (
    <span title={chip.title} className={`${chip.cls} ${className ?? ''}`}>
      <span className={`h-1 w-1 rounded-full ${chip.dot}`} />
      {label}
    </span>
  );
}
